import React from 'react';
import { Checkbox, DefaultButton, Dialog, DialogFooter, DialogType, Dropdown, IContextualMenuItem, IDropdownOption, IconButton, Panel, PanelType, Pivot, PivotItem, PrimaryButton, Stack, StackItem, TextField } from '@fluentui/react';
import { toast } from 'react-toastify';
import {
    useAddStorefrontOrderShipmentMutation,
    useDeleteStorefrontOrderShipmentMutation,
    useGetStorefrontOrderQuery,
    useGetStorefrontOrderStatusesQuery,
    useProcessStorefrontOrderMutation,
    useUpdateStorefrontOrderShipmentMutation,
    useUpdateStorefrontOrderStatusMutation
} from '../../../store/endpoints/StorefrontSettingsEndpoints';
import { humanizeRecordType, IRecordLink, useConvertStorefrontOrderToSalesOrderMutation, useCreateStorefrontOrderOpportunityMutation, useGetStorefrontOrderLinksQuery, useGetStorefrontOrderOpportunityCreationOptionsQuery, useLazyResolveRecordUrlQuery } from '../../../store/endpoints/RecordLinksEndpoints';
import { IsMutationSuccess } from '../../../logic/MutationTypeChecker';
import StorefrontLabel from '../StorefrontLabel';
import StorefrontOrderProductsTable from './StorefrontOrderProductsTable';
import StorefrontOrderBundlesTable from './StorefrontOrderBundlesTable';
import StorefrontOrderQuestionsTable from './StorefrontOrderQuestionsTable';
import StorefrontOrderActivities from './StorefrontOrderActivities';
import { IOrderShipment, IStorefrontOrder, IStorefrontOrderAddress } from '../../../models/Storefront';
import DropdownField from '../../Common/DropdownField';
import CurrencyDisplay from '../../Currency/CurrencyDisplay';

interface IStorefrontOrderPanel {
    order?: string;
    onDismiss: () => void;
    initialTab?: string;
}

// "SalesOrder" -> "Sales Order", for record-type-aware action labels like "Open Sales Order in {folio}".
const titleCaseRecordType = (recordType: string): string =>
    humanizeRecordType(recordType).replace(/\b\w/g, c => c.toUpperCase());

// The order stores the processor as the string enum ("STRIPE"); show it as a name ("Stripe").
const formatPaymentMethod = (paymentMethod: string): string =>
    paymentMethod.charAt(0).toUpperCase() + paymentMethod.slice(1).toLowerCase();

const PROCESS_STEPS: { key: string; label: string }[] = [
    { key: 'email_customer', label: 'Email to Customer' },
    { key: 'email_msp', label: 'Email to Organization' },
    { key: 'opportunity', label: 'Opportunity Creation' },
    { key: 'procurement_demand_items', label: 'Procurement Demand Items' },
    { key: 'procurement_orders', label: 'Procurement Orders' },
    { key: 'webhooks', label: 'Webhooks' },
];

const StorefrontOrderPanel: React.FC<IStorefrontOrderPanel> = (props) => {
    const [processOrder] = useProcessStorefrontOrderMutation();
    const [processing, setProcessing] = React.useState<boolean>(false);
    const statuses = useGetStorefrontOrderStatusesQuery();
    const [statusOptions, setStatusOptions] = React.useState<{ key: string, text: string }[]>([]);
    const [updateOrderStatus] = useUpdateStorefrontOrderStatusMutation();
    const [status, setStatus] = React.useState<string | undefined>();
    const order = useGetStorefrontOrderQuery(props.order ?? "", {skip: !props.order || props.order === ''});
    const [selectedTab, setSelectedTab] = React.useState<string>(props.initialTab ?? 'details');
    const [showStepsDialog, setShowStepsDialog] = React.useState<boolean>(false);
    const [selectedSteps, setSelectedSteps] = React.useState<string[]>(PROCESS_STEPS.map(s => s.key));

    React.useEffect(() => {
        setSelectedTab(props.initialTab ?? 'details');
    }, [props.order, props.initialTab]);
    const links = useGetStorefrontOrderLinksQuery(props.order ?? "", {skip: !props.order || props.order === ''});
    const [resolveRecordUrl] = useLazyResolveRecordUrlQuery();
    const [convertToSalesOrder, convertResult] = useConvertStorefrontOrderToSalesOrderMutation();
    const opportunityCreation = useGetStorefrontOrderOpportunityCreationOptionsQuery(props.order ?? "", {skip: !props.order || props.order === ''});
    const [createOpportunity, createOpportunityResult] = useCreateStorefrontOrderOpportunityMutation();

    const openRecord = async (link: IRecordLink) => {
        try {
            const res = await resolveRecordUrl({folioId: link.folioId, recordType: link.recordType, recordId: link.recordId}).unwrap();
            if (res.supported && res.url) {
                window.open(res.url, '_blank', 'noopener');
            } else {
                toast.error(`This record can't be opened in ${link.folioName ?? 'the CRM'}`);
            }
        } catch {
            toast.error('Failed to open record');
        }
    };

    const runConvertToSalesOrder = async (folioId: string, folioName?: string) => {
        if (!props.order) return;
        // Conversion can take a minute or two, so keep a persistent loading toast up until it resolves.
        const toastId = toast.loading(`Converting to Sales Order in ${folioName ?? 'the CRM'}…`);
        try {
            const res = await convertToSalesOrder({orderId: props.order, folioId}).unwrap();
            if (res.success) {
                toast.update(toastId, {render: `Opportunity converted to sales order in ${folioName ?? 'the CRM'}`, type: 'success', isLoading: false, autoClose: 5000});
                void links.refetch();
            } else {
                toast.update(toastId, {render: res.error ?? 'Failed to convert to sales order', type: 'error', isLoading: false, autoClose: 5000});
            }
        } catch (e) {
            const message = (e as { data?: { message?: string } })?.data?.message;
            toast.update(toastId, {render: message ?? 'Failed to convert to sales order', type: 'error', isLoading: false, autoClose: 5000});
        }
    };

    const runCreateOpportunity = async (folioId: string, folioName?: string) => {
        if (!props.order) return;
        // Opportunity creation runs the target folio's CRM extension, which can take a little while, so keep a
        // persistent loading toast up until it resolves.
        const toastId = toast.loading(`Creating Opportunity in ${folioName ?? 'the CRM'}…`);
        try {
            const res = await createOpportunity({orderId: props.order, folioId}).unwrap();
            if (res.success) {
                toast.update(toastId, {render: `Opportunity created in ${res.folioName ?? folioName ?? 'the CRM'}`, type: 'success', isLoading: false, autoClose: 5000});
                // The new opportunity changes the order's links (and unlocks the convert-to-sales-order action).
                void links.refetch();
                void opportunityCreation.refetch();
            } else {
                toast.update(toastId, {render: res.error ?? 'Failed to create opportunity', type: 'error', isLoading: false, autoClose: 5000});
            }
        } catch (e) {
            const message = (e as { data?: { message?: string } })?.data?.message;
            toast.update(toastId, {render: message ?? 'Failed to create opportunity', type: 'error', isLoading: false, autoClose: 5000});
        }
    };

    const linkData = links.data ?? [];
    // A sales order already exists in a CRM if there's a SalesOrder link from the same folio.
    const folioHasSalesOrder = (folioId: string) => linkData.some(link => link.recordType === 'SalesOrder' && link.folioId === folioId);

    const actionItems: IContextualMenuItem[] = linkData.map(link => ({
        key: `open-${link.folioId}-${link.recordType}-${link.recordId}`,
        text: `Open ${titleCaseRecordType(link.recordType)} in ${link.folioName ?? 'CRM'}`,
        iconProps: {iconName: 'OpenInNewWindow'},
        disabled: !link.supported,
        title: link.supported ? undefined : `${link.folioName ?? 'This folio'} does not support direct linking to ${humanizeRecordType(link.recordType)}`,
        onClick: link.supported ? () => { void openRecord(link); } : undefined
    }));

    // Offer a conversion per opportunity whose CRM supports it. No opportunity -> no convert action.
    linkData
        .filter(link => link.recordType === 'Opportunity' && link.canConvertToSalesOrder)
        .forEach(link => {
            const converted = folioHasSalesOrder(link.folioId);
            const converting = convertResult.isLoading && convertResult.originalArgs?.folioId === link.folioId;
            actionItems.push({
                key: `convert-${link.folioId}`,
                text: converting ? 'Converting…' : `Convert to Sales Order in ${link.folioName ?? 'CRM'}`,
                iconProps: {iconName: 'Receipt'},
                disabled: convertResult.isLoading || converted,
                title: converted ? `This opportunity has already been converted to a sales order in ${link.folioName ?? 'the CRM'}` : undefined,
                onClick: () => { void runConvertToSalesOrder(link.folioId, link.folioName); }
            });
        });

    // Offer creating an opportunity in every installed folio that supports it (has a storefrontOpportunity
    // sub-extension) and doesn't already have one for this order — so orgs running multiple CRMs can create in
    // any of them. Added to the top, in reverse so the folios stay in their returned order.
    (opportunityCreation.data ?? [])
        .filter(option => !option.alreadyExists)
        .reverse()
        .forEach(option => {
            const creating = createOpportunityResult.isLoading && createOpportunityResult.originalArgs?.folioId === option.folioId;
            actionItems.unshift({
                key: `create-opportunity-${option.folioId}`,
                text: creating ? 'Creating Opportunity…' : `Create Opportunity in ${option.folioName ?? 'CRM'}`,
                iconProps: {iconName: 'AddTo'},
                disabled: createOpportunityResult.isLoading,
                onClick: () => { void runCreateOpportunity(option.folioId, option.folioName); }
            });
        });

    React.useEffect(() => {
        if(order.data) {
            setStatus(order.data.status);
        }
    }, [order.data])

    React.useEffect(() => {
        if (statuses.data == null) return;
        setStatusOptions(Object.entries(statuses.data).map(([key,text]) => ({ key, text})));
    }, [statuses.data])

    const openProcessDialog = () => {
        setSelectedSteps(PROCESS_STEPS.map(s => s.key));
        setShowStepsDialog(true);
    };

    const toggleStep = (key: string, checked: boolean) => {
        setSelectedSteps(prev =>
            checked ? [...prev, key] : prev.filter(s => s !== key)
        );
    };

    const confirmProcess = async () => {
        if (props.order == null || selectedSteps.length === 0) return;
        setShowStepsDialog(false);
        setProcessing(true);
        await processOrder({ id: props.order, steps: selectedSteps });
        setProcessing(false);
    };

    const updateStatus = async (event: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        const res = await updateOrderStatus({id: props.order ?? '', status: option?.key as string});
        if (IsMutationSuccess(res)) {
            setStatus(option?.key as string);
        }
    }

    const headerText = order?.data?.orderNumber ? `Order ${order.data.orderNumber.toString()}` : 'Loading...';

    return (
        <>
        <Panel
            isOpen={props.order != null}
            onDismiss={props.onDismiss}
            type={PanelType.medium}
            headerText={headerText}
            // Less-common actions live in a three-dots menu in the header (left of the X), matching the
            // Procurement Order panel. The footer keeps the primary Process action and Cancel.
            onRenderHeader={() => (
                <div className='ms-Panel-header' style={{paddingLeft: '24px', paddingRight: '0.5em', alignSelf: 'flex-start', flexGrow: 1}}>
                    <div className='ms-Panel-headerText'>
                        <Stack horizontal verticalAlign='center'>
                            <StackItem>
                                <span style={{
                                    fontFamily: '"Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
                                    fontSize: '20px',
                                    fontWeight: 600,
                                    color: 'rgb(50, 49, 48)',
                                    lineHeight: '27px',
                                    overflowWrap: 'break-word',
                                    wordBreak: 'break-word',
                                    hyphens: 'auto'
                                }}>
                                    {headerText}
                                </span>
                            </StackItem>
                            {actionItems.length > 0 && <StackItem styles={{ root: { marginLeft: 'auto' }}}>
                                <IconButton
                                    iconProps={{ iconName: 'MoreVertical' }}
                                    title='Actions'
                                    ariaLabel='Actions'
                                    onRenderMenuIcon={() => null}
                                    menuProps={{ items: actionItems }}
                                />
                            </StackItem>}
                        </Stack>
                    </div>
                </div>
            )}
            isFooterAtBottom={true}
            onRenderFooterContent={() => (
                <Stack horizontal tokens={{childrenGap:'1em'}}>
                    <PrimaryButton text={processing ? 'Processing' : 'Process'} onClick={openProcessDialog} disabled={processing} />
                    <DefaultButton text='Cancel' onClick={props.onDismiss} />
                </Stack>
            )}
            >
            <Dialog
                hidden={!showStepsDialog}
                onDismiss={() => setShowStepsDialog(false)}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: 'Select Steps to Run',
                    subText: 'Choose which processing steps to execute for this order.',
                }}
                modalProps={{ isBlocking: true }}
            >
                <Stack tokens={{ childrenGap: '0.75em' }} style={{ marginBottom: '1em' }}>
                    {PROCESS_STEPS.map(step => (
                        <Checkbox
                            key={step.key}
                            label={step.label}
                            checked={selectedSteps.includes(step.key)}
                            onChange={(_, checked) => toggleStep(step.key, checked ?? false)}
                        />
                    ))}
                </Stack>
                <DialogFooter>
                    <PrimaryButton
                        text='Run'
                        onClick={confirmProcess}
                        disabled={selectedSteps.length === 0}
                    />
                    <DefaultButton text='Cancel' onClick={() => setShowStepsDialog(false)} />
                </DialogFooter>
            </Dialog>
            {order?.data && (
                <Pivot selectedKey={selectedTab} onLinkClick={(item) => setSelectedTab(item?.props.itemKey ?? 'details')}>
                    <PivotItem headerText='Details' itemKey='details'>
                        <p>
                            <span style={{fontWeight: 'bold'}}>Order Number: </span>
                            {order.data.orderNumber}
                        </p>
                        <p>
                            <span style={{fontWeight: 'bold'}}>Storefront: </span>
                            {order.data.storefront && <StorefrontLabel storefrontId={order.data.storefront} />}
                        </p>
                        <p>
                            <span style={{fontWeight: 'bold'}}>Email Address: </span>
                            {order.data.emailAddress}
                        </p>
                        <p>
                            <span style={{fontWeight: 'bold'}}>Order Date: </span>
                            {order.data.createDate && new Date(order.data.createDate).toLocaleString()}
                        </p>
                        {order.data.paymentMethod && order.data.paymentMethod !== 'NONE' && (
                            <p>
                                <span style={{fontWeight: 'bold'}}>Paid With: </span>
                                {formatPaymentMethod(order.data.paymentMethod)}
                            </p>
                        )}
                        <Dropdown
                            label='Status'
                            options={statusOptions}
                            selectedKey={status}
                            onChange={updateStatus}
                            />
                        {order.data.products && order.data.products.length > 0 && (<>
                            <h3>Products</h3>
                            <StorefrontOrderProductsTable products={order.data.products} />
                        </>)}
                        {order.data.bundles && order.data.bundles.length > 0 && (<>
                            <h3>Bundles</h3>
                            <StorefrontOrderBundlesTable bundles={order.data.bundles} />
                        </>)}
                        <OrderTotalsSection order={order.data} />
                    </PivotItem>
                    <PivotItem headerText='Shipping' itemKey='shipping'>
                        <ShipmentTrackingSection
                            orderId={order.data.id}
                            storefrontId={order.data.storefront ?? ''}
                            shipments={order.data.shipments ?? []}
                        />
                    </PivotItem>
                    <PivotItem headerText='Customer Information' itemKey='customer'>
                        <AddressSection title='Shipping Address' address={order.data.shippingAddress} />
                        <AddressSection title='Billing Address' address={order.data.billingAddress} />
                    </PivotItem>
                    {order.data.questions && order.data.questions.length > 0 && (
                        <PivotItem headerText='Order Questions' itemKey='questions'>
                            <StorefrontOrderQuestionsTable order={order.data} />
                        </PivotItem>
                    )}
                    <PivotItem headerText='Activity' itemKey='activity'>
                        <StorefrontOrderActivities orderId={order.data.id} />
                    </PivotItem>
                </Pivot>
            )}
        </Panel>
        </>
    );
}

const CARRIER_OPTIONS: IDropdownOption[] = [
    { key: 'FEDEX', text: 'FedEx' },
    { key: 'UPS', text: 'UPS' },
    { key: 'USPS', text: 'USPS' },
    { key: 'DHL', text: 'DHL' },
    { key: 'AMAZON', text: 'Amazon Logistics' },
    { key: 'ONTRAC', text: 'OnTrac' },
    { key: 'OTHER', text: 'Other' },
];

const KNOWN_CARRIER_KEYS = new Set(CARRIER_OPTIONS.filter(o => o.key !== 'OTHER').map(o => o.key as string));

const CARRIER_TRACKING_URL: Partial<Record<string, (n: string) => string>> = {
    FEDEX: n => `https://www.fedex.com/fedextrack/?trknbr=${n}`,
    UPS: n => `https://www.ups.com/track?tracknum=${n}`,
    USPS: n => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${n}`,
    DHL: n => `https://www.dhl.com/us-en/home/tracking/tracking-express.html?submit=1&tracking-id=${n}`,
    ONTRAC: n => `https://www.ontrac.com/tracking/?number=${n}`,
};

function resolveCarrierKey(carrier?: string): string | undefined {
    if (!carrier) return undefined;
    return KNOWN_CARRIER_KEYS.has(carrier) ? carrier : 'OTHER';
}

interface IShipmentTrackingSectionProps {
    orderId: string;
    storefrontId: string;
    shipments: IOrderShipment[];
}

interface IShipmentFormState {
    carrierKey?: string;
    customCarrier: string;
    trackingNumber: string;
    trackingUrl: string;
}

function shipmentToFormState(s: IOrderShipment): IShipmentFormState {
    return {
        carrierKey: resolveCarrierKey(s.shipmentCarrier),
        customCarrier: s.shipmentCarrier && !KNOWN_CARRIER_KEYS.has(s.shipmentCarrier) ? s.shipmentCarrier : '',
        trackingNumber: s.shipmentTrackingNumber ?? '',
        trackingUrl: s.shipmentTrackingUrl ?? '',
    };
}

const emptyFormState = (): IShipmentFormState => ({ carrierKey: undefined, customCarrier: '', trackingNumber: '', trackingUrl: '' });

const ShipmentForm: React.FC<{
    state: IShipmentFormState;
    onChange: (s: IShipmentFormState) => void;
}> = ({ state, onChange }) => {
    const handleCarrierChange = (_: React.FormEvent<HTMLDivElement>, option?: IDropdownOption) => {
        const key = option?.key as string;
        const generator = CARRIER_TRACKING_URL[key];
        const trackingUrl = generator && state.trackingNumber ? generator(state.trackingNumber) : (key !== 'OTHER' ? '' : state.trackingUrl);
        onChange({ ...state, carrierKey: key, customCarrier: key !== 'OTHER' ? '' : state.customCarrier, trackingUrl });
    };
    const handleTrackingNumberChange = (_: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, value?: string) => {
        const num = value ?? '';
        const generator = state.carrierKey && state.carrierKey !== 'OTHER' ? CARRIER_TRACKING_URL[state.carrierKey] : undefined;
        onChange({ ...state, trackingNumber: num, trackingUrl: generator && num ? generator(num) : state.trackingUrl });
    };
    return (
        <div>
            <DropdownField label='Carrier' options={CARRIER_OPTIONS} selectedKey={state.carrierKey} onChange={handleCarrierChange} placeholder='Select a carrier' />
            {state.carrierKey === 'OTHER' && (
                <div style={{ marginTop: '0.5em' }}>
                    <TextField label='Carrier Name' value={state.customCarrier} onChange={(_, v) => onChange({ ...state, customCarrier: v ?? '' })} placeholder='Enter carrier name' />
                </div>
            )}
            <div style={{ marginTop: '0.5em' }}>
                <TextField label='Tracking Number' value={state.trackingNumber} onChange={handleTrackingNumberChange} placeholder='Enter tracking number' />
            </div>
            <div style={{ marginTop: '0.5em' }}>
                <TextField label='Tracking URL' value={state.trackingUrl} onChange={(_, v) => onChange({ ...state, trackingUrl: v ?? '' })} placeholder='Enter tracking URL' />
            </div>
            {state.trackingUrl && (
                <div style={{ marginTop: '0.5em' }}>
                    <a href={state.trackingUrl} target='_blank' rel='noopener noreferrer'>Track Shipment →</a>
                </div>
            )}
        </div>
    );
};

const ShipmentTrackingSection: React.FC<IShipmentTrackingSectionProps> = ({ orderId, storefrontId, shipments }) => {
    const [addShipment] = useAddStorefrontOrderShipmentMutation();
    const [updateShipment] = useUpdateStorefrontOrderShipmentMutation();
    const [deleteShipment] = useDeleteStorefrontOrderShipmentMutation();
    const [editingId, setEditingId] = React.useState<string | null>(null);
    const [editForm, setEditForm] = React.useState<IShipmentFormState>(emptyFormState());
    const [addForm, setAddForm] = React.useState<IShipmentFormState>(emptyFormState());
    const [showAddForm, setShowAddForm] = React.useState(false);
    const [saving, setSaving] = React.useState(false);

    const startEdit = (s: IOrderShipment) => {
        setEditingId(s.shipmentTrackingId ?? null);
        setEditForm(shipmentToFormState(s));
    };

    const cancelEdit = () => { setEditingId(null); setEditForm(emptyFormState()); };

    const handleSaveEdit = async () => {
        if (!editingId) return;
        setSaving(true);
        const carrier = editForm.carrierKey === 'OTHER' ? editForm.customCarrier : editForm.carrierKey;
        await updateShipment({ id: orderId, storefront: storefrontId, shipmentTrackingId: editingId, carrier, trackingNumber: editForm.trackingNumber || undefined, trackingUrl: editForm.trackingUrl || undefined });
        setSaving(false);
        cancelEdit();
    };

    const handleDelete = async (shipmentTrackingId: string) => {
        await deleteShipment({ id: orderId, storefront: storefrontId, shipmentTrackingId });
    };

    const handleAdd = async () => {
        setSaving(true);
        const carrier = addForm.carrierKey === 'OTHER' ? addForm.customCarrier : addForm.carrierKey;
        await addShipment({ id: orderId, storefront: storefrontId, carrier, trackingNumber: addForm.trackingNumber || undefined, trackingUrl: addForm.trackingUrl || undefined });
        setSaving(false);
        setAddForm(emptyFormState());
        setShowAddForm(false);
    };

    return (
        <div>
            <Stack horizontal horizontalAlign='space-between' verticalAlign='center'>
                <h3 style={{ margin: '0.5em 0' }}>Shipments</h3>
                <IconButton text='Add Shipment' iconProps={{iconName: "Add"}} onClick={() => { setShowAddForm(true); setAddForm(emptyFormState()); }} disabled={showAddForm} />
            </Stack>

            {shipments.length === 0 && !showAddForm && (
                <p style={{ color: 'gray' }}>No shipments recorded.</p>
            )}

            {shipments.map((s, i) => (
                <div key={s.shipmentTrackingId ?? i} style={{ border: '1px solid #e1dfdd', borderRadius: 4, padding: '0.75em', marginTop: '0.75em' }}>
                    {editingId === s.shipmentTrackingId ? (
                        <>
                            <ShipmentForm state={editForm} onChange={setEditForm} />
                            <Stack horizontal tokens={{ childrenGap: '0.5em' }} style={{ marginTop: '0.75em' }}>
                                <PrimaryButton text={saving ? 'Saving...' : 'Save'} onClick={handleSaveEdit} disabled={saving} />
                                <DefaultButton text='Cancel' onClick={cancelEdit} />
                            </Stack>
                        </>
                    ) : (
                        <Stack horizontal horizontalAlign='space-between' verticalAlign='start'>
                            <div>
                                {s.shipmentCarrier && <p style={{ margin: '0 0 0.25em' }}><strong>Carrier:</strong> {s.shipmentCarrier}</p>}
                                {s.shipmentTrackingNumber && <p style={{ margin: '0 0 0.25em' }}><strong>Tracking #:</strong> {s.shipmentTrackingNumber}</p>}
                                {s.shipmentTrackingUrl && <p style={{ margin: 0 }}><a href={s.shipmentTrackingUrl} target='_blank' rel='noopener noreferrer'>Track Shipment →</a></p>}
                            </div>
                            <Stack horizontal tokens={{ childrenGap: '0.25em' }}>
                                <IconButton iconProps={{ iconName: 'Edit' }} title='Edit' onClick={() => startEdit(s)} />
                                <IconButton iconProps={{ iconName: 'Delete' }} title='Remove' onClick={() => s.shipmentTrackingId && handleDelete(s.shipmentTrackingId)} />
                            </Stack>
                        </Stack>
                    )}
                </div>
            ))}

            {showAddForm && (
                <div style={{ border: '1px solid #e1dfdd', borderRadius: 4, padding: '0.75em', marginTop: '0.75em' }}>
                    <ShipmentForm state={addForm} onChange={setAddForm} />
                    <Stack horizontal tokens={{ childrenGap: '0.5em' }} style={{ marginTop: '0.75em' }}>
                        <PrimaryButton text={saving ? 'Saving...' : 'Add'} onClick={handleAdd} disabled={saving} />
                        <DefaultButton text='Cancel' onClick={() => setShowAddForm(false)} />
                    </Stack>
                </div>
            )}
        </div>
    );
};

// A totals summary is not a data table: it has no rows to scan or pick, so it deliberately avoids the
// `data-table` class, whose per-row rules (hover highlight, a rule under every line) would invite a click
// that does nothing and box the three components apart. Only the Total is ruled off, from its inputs.
const totalsLabelStyle: React.CSSProperties = {textAlign: 'right', padding: '4px 1em 4px 0'};
const totalsValueStyle: React.CSSProperties = {textAlign: 'right', padding: '4px 0'};
const totalsDividerStyle: React.CSSProperties = {borderTop: '1px solid rgb(207, 220, 181)'};

/**
 * What the customer was actually charged, broken out. The order total alone can't be reconciled against the
 * line items without it — a Stripe order's tax comes back from the payment session rather than the cart, so
 * the difference between subtotal and total is otherwise unexplained.
 */
const OrderTotalsSection: React.FC<{order: IStorefrontOrder}> = ({order}) => (
    <table style={{marginTop: '1em', marginLeft: 'auto', width: 'auto', minWidth: '260px', borderCollapse: 'collapse'}}>
        <tbody>
            <tr>
                <td style={totalsLabelStyle}>Subtotal:</td>
                <td style={totalsValueStyle}><CurrencyDisplay value={order.subtotal} /></td>
            </tr>
            <tr>
                <td style={totalsLabelStyle}>Shipping:</td>
                <td style={totalsValueStyle}><CurrencyDisplay value={order.shipping ?? 0} /></td>
            </tr>
            <tr>
                <td style={totalsLabelStyle}>Tax:</td>
                <td style={totalsValueStyle}><CurrencyDisplay value={order.tax ?? 0} /></td>
            </tr>
            <tr>
                <td style={{...totalsLabelStyle, ...totalsDividerStyle, fontWeight: 'bold'}}>Total:</td>
                <td style={{...totalsValueStyle, ...totalsDividerStyle, fontWeight: 'bold'}}>
                    <CurrencyDisplay value={order.total} />
                </td>
            </tr>
        </tbody>
    </table>
);

const AddressSection: React.FC<{title: string; address: IStorefrontOrderAddress}> = ({title, address}) => {
    const hasData = address && Object.values(address).some(v => v);
    return (
        <div>
            <h3>{title}</h3>
            {hasData ? (
                <table>
                    <tbody>
                        {address.name && <tr><td style={{fontWeight: 'bold', paddingRight: '1em'}}>Name</td><td>{address.name}</td></tr>}
                        {address.address1 && <tr><td style={{fontWeight: 'bold', paddingRight: '1em'}}>Address 1</td><td>{address.address1}</td></tr>}
                        {address.address2 && <tr><td style={{fontWeight: 'bold', paddingRight: '1em'}}>Address 2</td><td>{address.address2}</td></tr>}
                        {address.city && <tr><td style={{fontWeight: 'bold', paddingRight: '1em'}}>City</td><td>{address.city}</td></tr>}
                        {address.state && <tr><td style={{fontWeight: 'bold', paddingRight: '1em'}}>State</td><td>{address.state}</td></tr>}
                        {address.zipCode && <tr><td style={{fontWeight: 'bold', paddingRight: '1em'}}>Zip Code</td><td>{address.zipCode}</td></tr>}
                        {address.country && <tr><td style={{fontWeight: 'bold', paddingRight: '1em'}}>Country</td><td>{address.country}</td></tr>}
                        {address.phoneNumber && <tr><td style={{fontWeight: 'bold', paddingRight: '1em'}}>Phone</td><td>{address.phoneNumber}</td></tr>}
                    </tbody>
                </table>
            ) : (
                <p style={{color: 'gray'}}>No address on file.</p>
            )}
        </div>
    );
}

export default StorefrontOrderPanel;